import { Body_add_your_food_api_v1_meals_your_food_post } from "@/clients/models/Body_add_your_food_api_v1_meals_your_food_post";
import PurpleRadialGradientBackground from "@/components/background/purple-radial-gradient";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { StackedModal } from "@/components/modal/stacked-modal";
import { OutlinedButton } from "@/components/ui/buttons/OutlinedButton";
import { Counter } from "@/components/ui/counter";
import { MealTimeSelect } from "@/features/meal-plan/components/meal-time-select";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo, RefObject, useCallback, useState } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { AddRecipeTextareaPage, METHOD_PLACEHOLDER_TEXT } from "./add-recipe-textarea-page";

export interface AddRecipeFormValues {
  recipeName: string;
  mealTime: Body_add_your_food_api_v1_meals_your_food_post.meal_time | "";
  portionSize: number;
  ingredients: string;
  instructions: string;
}

export const DEFAULT_ADD_RECIPE_VALUES: AddRecipeFormValues = {
  recipeName: "",
  mealTime: "",
  portionSize: 1,
  ingredients: "",
  instructions: "",
};

interface AddRecipeTextModalProps {
  pageRef: RefObject<HTMLElement>;
  isOpen: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  control: Control<AddRecipeFormValues>;
  fixedMealTime?: Body_add_your_food_api_v1_meals_your_food_post.meal_time;
}

export const AddRecipeTextModal = memo(function AddRecipeTextModal({
  pageRef,
  isOpen,
  onDismiss,
  onConfirm,
  control,
  fixedMealTime,
}: AddRecipeTextModalProps) {
  const [isIngredientsPageOpen, setIsIngredientsPageOpen] = useState(false);
  const [isMethodPageOpen, setIsMethodPageOpen] = useState(false);

  const recipeName = useWatch({ control, name: "recipeName" });
  const mealTime = useWatch({ control, name: "mealTime" });
  const portionSize = useWatch({ control, name: "portionSize" });
  const selectedMealTime = fixedMealTime ?? mealTime;

  const disabledConfirmButton =
    !Boolean(recipeName?.trim()) || !Boolean(selectedMealTime) || portionSize < 1;

  const openIngredientsPage = useCallback(() => setIsIngredientsPageOpen(true), []);
  const closeIngredientsPage = useCallback(() => setIsIngredientsPageOpen(false), []);
  const openMethodPage = useCallback(() => setIsMethodPageOpen(true), []);
  const closeMethodPage = useCallback(() => setIsMethodPageOpen(false), []);

  return (
    <>
      <StackedModal
        pageRef={pageRef}
        isOpen={isOpen}
        title="Add your food"
        showHeader={true}
        showConfirmButton={true}
        onConfirm={onConfirm}
        disabledConfirmButton={disabledConfirmButton}
        onDidDismiss={onDismiss}
      >
        <PurpleRadialGradientBackground
          classNames={{
            container: cn("w-full flex flex-col gap-6 px-4 py-6 "),
            image: "bottom-0",
          }}
        >
          <TextFieldWithLabel
            control={control}
            name="recipeName"
            label="Food name"
            placeholder="Please enter the food's name."
            containerClassName="gap-2"
          />

          <div
            className={cn("items-end gap-4", {
              "grid grid-cols-[1fr_auto]": !fixedMealTime,
              "flex justify-start": fixedMealTime,
            })}
          >
            {!fixedMealTime && <MealTimeSelect control={control} />}
            <div className={cn("flex flex-col gap-2")}>
              <span className={cn(textVariants.inputLabel)}>Portion size</span>
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="portionSize"
                  rules={{ min: 1, max: 999 }}
                  render={({ field }) => (
                    <>
                      <span className={cn(textVariants.bodySingleLine)}>
                        {field.value} portion{field.value !== 1 ? "s" : ""}
                      </span>
                      <Counter
                        value={field.value}
                        onChange={field.onChange}
                        min={1}
                        className="shrink-0"
                        color="purple"
                      />
                    </>
                  )}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className={cn(textVariants.inputLabel)}>Your food Info</span>
            <div className="flex flex-col gap-3">
              <OutlinedButton
                color="gray"
                textCase="normal"
                borderRadius="wide-square"
                bgColor="white"
                className="text-[#7A28EE]"
                onClick={openIngredientsPage}
              >
                Add ingredients
              </OutlinedButton>
              <OutlinedButton
                color="gray"
                textCase="normal"
                borderRadius="wide-square"
                bgColor="white"
                className="text-[#7A28EE]"
                onClick={openMethodPage}
              >
                Add method (optional)
              </OutlinedButton>
            </div>
          </div>
        </PurpleRadialGradientBackground>
      </StackedModal>

      <AddRecipeTextareaPage<AddRecipeFormValues>
        pageRef={pageRef}
        isOpen={isIngredientsPageOpen}
        onDismiss={closeIngredientsPage}
        onConfirm={closeIngredientsPage}
        control={control}
        name="ingredients"
        subtitle="Ingredients"
      />

      <AddRecipeTextareaPage<AddRecipeFormValues>
        pageRef={pageRef}
        isOpen={isMethodPageOpen}
        onDismiss={closeMethodPage}
        onConfirm={closeMethodPage}
        control={control}
        name="instructions"
        subtitle="Method"
        placeholder={METHOD_PLACEHOLDER_TEXT}
      />
    </>
  );
});
